import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import EventSpeaker from "./routes/event/EventSpeaker";
import Home from "./routes/home/Home";
import useUser from "../hooks/user";
import Landing from "./routes/landing/Landing";
import Header from "./Header";
import { useCallback, useEffect, useState } from "react";
import Survey from "./common/Survey";
import { useLogEvent } from "../hooks/log";
import Loader from "./common/Loader";
import CreateOrEditRoom from "./routes/room/CreateOrEditRoom";
import Meeting from "./routes/meeting/Meeting";
import { useAxios } from "../hooks/axios";
import api from "../hooks/api";
import TeamsRouter from "./teams/Router";
import { OrganizationProvider } from "./context/organization/OrganizationProvider";
import RecordingPage from "./routes/recording/Recording";
import { RoomProvider } from "./context/room/RoomProvider";
import EventDetails from "./routes/event/EventDetails";
import EventAttendee from "./routes/event/EventAttendee";
import EventInterpreter from "./routes/event/EventInterpreter";
import Login from "./routes/login/Login";
import ByrdhouseBanner from "./common/ByrdhouseBanner";
import CustomerStories from "./routes/customer-stories/CustomerStories";
import LandingFAQ from "./routes/faq/LandingFAQ";
import LandingContentPage from "./routes/landing/LandingContentPage";
import PricingPage from "./routes/pricing/PricingPage";
import About from "./routes/about/About";
import { useIsInTeams } from "../hooks/teams";
import CanonicalManager from "./CanonicalManager";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Handle expected logins
function Authenticator(props: { children: React.ReactNode }) {
  const axios = useAxios();
  const login = api.useLogin(axios);

  const expectLogin = localStorage.getItem("EXPECT_LOGIN");
  const [loading, setLoading] = useState(!!expectLogin);
  const authenticate = useCallback(async () => {
    if (expectLogin) {
      setLoading(true);
      try {
        await login();
      } catch (error) {
        localStorage.clear();
        throw error;
      } finally {
        setLoading(false);
      }
    }
  }, [expectLogin, setLoading, login]);
  useEffect(() => {
    authenticate();
  }, [authenticate]);

  if (loading) {
    return <Loader />;
  }

  return <>{props.children}</>;
}

function RouteLogger() {
  const location = useLocation();

  const logEvent = useLogEvent();
  useEffect(() => {
    logEvent({ type: "visit_url", details: { path: location.pathname } });
  }, [logEvent, location.pathname]);

  return null;
}

function AuthenticatedRoutes() {
  return (
    <OrganizationProvider>
      <Routes>
        <Route
          path="*"
          element={
            <>
              <ByrdhouseBanner />
              <Header />
              <Home />
            </>
          }
        />
        <Route
          path="/create/:roomType"
          element={
            <>
              <Header />
              <CreateOrEditRoom />
            </>
          }
        />
        <Route
          path="/edit/:id"
          element={
            <RoomProvider>
              <Header />
              <CreateOrEditRoom />
            </RoomProvider>
          }
        />
        <Route
          path="/event/:id/details"
          element={
            <RoomProvider>
              <Header />
              <EventDetails />
            </RoomProvider>
          }
        />
      </Routes>
    </OrganizationProvider>
  );
}

function Router() {
  const [user] = useUser();
  const isInTeams = useIsInTeams();

  // isInTeams starts null while teams.app.initialize() is still pending -
  // treating that as "not in Teams" (via a `!isInTeams` check) would
  // briefly mount the non-Teams Authenticator for real Teams sessions. That
  // Authenticator also gates on EXPECT_LOGIN, so it fires the generic
  // (non-Teams) login instead of the Teams one, that call has no valid
  // token to send, and the resulting failure clears all of localStorage,
  // including EXPECT_LOGIN, straight after every Teams app load.
  if (isInTeams === null) {
    return <Loader />;
  }

  return (
    <>
      {!isInTeams ? (
        <Authenticator>
          <BrowserRouter>
            <CanonicalManager />
            <RouteLogger />
            <ScrollToTop />
            <main>
              <Routes>
                {user.auth ? (
                  <Route path="/*" element={<AuthenticatedRoutes />} />
                ) : (
                  <>
                    <Route path="/" element={<Landing />} />
                    <Route
                      path="/customer-stories"
                      element={
                        <>
                          <ByrdhouseBanner />
                          <Header landing={true} />
                          <LandingContentPage>
                            <CustomerStories />
                          </LandingContentPage>
                        </>
                      }
                    />
                    <Route
                      path="/faq"
                      element={
                        <>
                          <ByrdhouseBanner />
                          <Header landing={true} />
                          <LandingContentPage>
                            <LandingFAQ />
                          </LandingContentPage>
                        </>
                      }
                    />
                    <Route
                      path="/about"
                      element={
                        <>
                          <ByrdhouseBanner />
                          <Header landing={true} />
                          <LandingContentPage>
                            <About />
                          </LandingContentPage>
                        </>
                      }
                    />
                    <Route
                      path="/pricing"
                      element={
                        <>
                          <ByrdhouseBanner />
                          <Header landing={true} />
                          <LandingContentPage>
                            <PricingPage />
                          </LandingContentPage>
                        </>
                      }
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Login signup={true} />} />
                  </>
                )}
                <Route
                  path="/meeting/:id"
                  element={
                    <RoomProvider>
                      <Header />
                      <Meeting />
                    </RoomProvider>
                  }
                />
                <Route
                  path="/meeting/:id/survey"
                  element={
                    <>
                      <Header />
                      <Survey />
                    </>
                  }
                />

                <Route
                  path="/event/:id/speaker"
                  element={
                    <>
                      <Header />
                      <RoomProvider>
                        <EventSpeaker />
                      </RoomProvider>
                    </>
                  }
                />
                <Route
                  path="/event/:id"
                  element={
                    <>
                      <RoomProvider>
                        <EventAttendee />
                      </RoomProvider>
                    </>
                  }
                />
                <Route
                  path="/event/:id/interpreter"
                  element={
                    <>
                      <Header />
                      <RoomProvider>
                        <EventInterpreter />
                      </RoomProvider>
                    </>
                  }
                />
                <Route
                  path="/event/:id/survey"
                  element={
                    <>
                      <Header />
                      <Survey />
                    </>
                  }
                />

                <Route
                  path="/recording/:id"
                  element={
                    <>
                      <Header />
                      <RecordingPage />
                    </>
                  }
                />

                <Route path="/*" element={<Landing />} />
              </Routes>
            </main>
          </BrowserRouter>
        </Authenticator>
      ) : (
        // Teams tab app routes
        <TeamsRouter />
      )}
    </>
  );
}

export default Router;
